import React, { useRef, useEffect } from 'react';
import { OrbitControls as DreiOrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import type { ViewType } from '../../utils/workspace.types';

interface EnhancedOrbitControlsProps {
  currentView: ViewType;
  isNodeCreationMode: boolean;
  zoomToCursor: boolean;
}

/**
 * Enhanced OrbitControls Component
 * 
 * ENGINEERING SOFTWARE STYLE - Matches STAAD.Pro camera controls
 * 
 * Requirements:
 * 1. LOCKED VIEWS (Front, Top, Side): Right-click drag for pan only, NO rotation
 * 2. ISOMETRIC VIEW: True spherical orbit with left-click drag + full controls
 * 
 * Features:
 * - Full 360° rotation in isometric view (no angle constraints)
 * - Smooth damping for professional feel
 * - Engineering-style mouse mappings
 * - View-dependent control configuration
 * - Smart target tracking (orbit around model center)
 */
export const EnhancedOrbitControls: React.FC<EnhancedOrbitControlsProps> = ({
  currentView,
  isNodeCreationMode,
  zoomToCursor
}) => {
  const controlsRef = useRef<OrbitControlsImpl>(null);

  // Determine if current view is locked (2D orthographic views)
  const isLockedView = ['front', 'top', 'side'].includes(currentView);
  const isIsometricView = currentView === 'isometric';

  useEffect(() => {
    if (!controlsRef.current || isNodeCreationMode) return;

    const controls = controlsRef.current;

    // Configure controls based on view type
    if (isLockedView) {
      // LOCKED VIEWS: Pan only, no rotation
      controls.enableRotate = false;
      controls.enablePan = true;
      
      // Reset angle constraints for locked views
      controls.minPolarAngle = 0;
      controls.maxPolarAngle = Math.PI;
      controls.minAzimuthAngle = -Infinity;
      controls.maxAzimuthAngle = Infinity;
    } else if (isIsometricView) {
      // ISOMETRIC VIEW: Full spherical rotation enabled
      controls.enableRotate = true;
      controls.enablePan = true;
      
      // Full 360° rotation - NO CONSTRAINTS
      controls.minPolarAngle = 0;              // Can look straight down
      controls.maxPolarAngle = Math.PI;        // Can look straight up
      controls.minAzimuthAngle = -Infinity;    // Unlimited horizontal rotation
      controls.maxAzimuthAngle = Infinity;
    }

    controls.update();
  }, [currentView, isNodeCreationMode, isLockedView, isIsometricView]);

  return (
    <>
      {!isNodeCreationMode && (
        <DreiOrbitControls
          ref={controlsRef}
          makeDefault
          
          // Core settings - Professional smooth feel
          enableDamping={true}
          dampingFactor={0.05}
          
          // Rotation settings (controlled by view type)
          enableRotate={isIsometricView}
          rotateSpeed={0.6}
          autoRotate={false}
          
          // Zoom settings
          enableZoom={false} // Handled centrally by CameraController
          zoomSpeed={1.2}
          zoomToCursor={zoomToCursor}
          
          // Pan settings
          enablePan={true}
          panSpeed={5}
          screenSpacePanning={true}
          keyPanSpeed={7}
          
          // Distance limits (increased for large structures)
          minDistance={0.5}      // Closer inspection (reduced from 1)
          maxDistance={50000}    // Much larger structures (increased from 10,000)
          
          // Angle limits (set dynamically in useEffect)
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
          minAzimuthAngle={-Infinity}
          maxAzimuthAngle={Infinity}
          
          // Mouse button configuration - ENGINEERING STYLE
          mouseButtons={{
            LEFT: THREE.MOUSE.ROTATE,    // Left-click: Rotate (only in isometric)
            MIDDLE: THREE.MOUSE.DOLLY,   // Middle-click: Zoom (handled by CameraController)
            RIGHT: THREE.MOUSE.PAN       // Right-click: Pan (works in ALL views)
          }}
          
          // Touch controls for mobile
          touches={{
            ONE: THREE.TOUCH.ROTATE,     // One finger: Rotate
            TWO: THREE.TOUCH.DOLLY_PAN   // Two fingers: Zoom + Pan
          }}
          
          // Target (orbit center)
          target={[0, 0, 0]}
        />
      )}
    </>
  );
};
